import { Injectable } from '@angular/core';
import {
  Action, Selector, State, StateContext,
} from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { ApolloQueryResult } from '@apollo/client/core';
import { SortEnum } from '@core/enums/sort.enum';
import {
  BasePaginatedEntityListState,
  BaseSortedEntityListState,
  BaseSortedEntityListStateWithDateFilter,
} from '@core/interfaces/base-entity-list-state.interface';
import {
  getDefaultPaginatedEntityListStateWithDateFilter,
  getDefaultSortedEntityListState,
  getDefaultSortedEntityListStateWithDateFilter,
} from '@core/helper/state-helper';
import { DateRange, NgbDateRange } from '@core/interfaces/date-filter.interface';
import { OffsetPagination } from '@core/interfaces/pagination-params.interface';
import { gateDateRangeFromNgbDateRange } from '@core/helper/date-helper';
import {
  SalaryCalculation,
  SalaryCalculationFilter,
  SalaryCalculationResponse,
} from '@core/interfaces/salary-calculation.interface';
import {
  GetSalaryCalculations,
  SetSalaryCalculationDateFilter,
  SetSalaryCalculationFilter,
  SetSalaryCalculationPaginationParams,
  SetSalaryCalculationSort,
} from '@core/store/action/salary-calculation.action';
import { SalaryCalculationService } from '@core/services/salary-calculation.service';
import { SortInterface } from '@core/interfaces/get-list.interface';

export interface SalaryCalculationStateModel extends BaseSortedEntityListStateWithDateFilter<
  SalaryCalculation,
  SalaryCalculationFilter
> {
  error: string | null;
  rawDateFilter: NgbDateRange;
  sort: SortInterface;
}

@State<SalaryCalculationStateModel>({
  name: 'SalaryCalculationState',
  defaults: {
    ...getDefaultSortedEntityListStateWithDateFilter<SalaryCalculationFilter, SalaryCalculation>(),
  },
})
@Injectable()
export class SalaryCalculationState {
  constructor(private salaryCalculationService: SalaryCalculationService) {}

  @Selector()
  static getSalaryCalculations(state: SalaryCalculationStateModel): SalaryCalculation[] {
    return state.list;
  }

  @Selector()
  static getSalaryCalculationsTotal(state: SalaryCalculationStateModel): number {
    return state.total;
  }

  @Selector()
  static getSalaryCalculationsFilter(state: SalaryCalculationStateModel): SalaryCalculationFilter {
    return state.filter;
  }

  @Selector()
  static getSalaryCalculationSearchFilter(state: SalaryCalculationStateModel): string | undefined {
    return state.filter.search;
  }

  @Selector()
  static getRawDateFilter(state: SalaryCalculationStateModel): NgbDateRange {
    return state.rawDateFilter;
  }

  @Selector()
  static getDateFilter(state: SalaryCalculationStateModel): DateRange {
    return gateDateRangeFromNgbDateRange(state.rawDateFilter);
  }

  @Selector()
  static getSalaryCalculationPaginationParams(state: SalaryCalculationStateModel): OffsetPagination {
    return state.pagination;
  }

  @Selector()
  static getSalaryCalculationSort(state: SalaryCalculationStateModel): SortInterface {
    return state.sort;
  }

  @Action(GetSalaryCalculations)
  public getListSalaryCalculations(
    ctx: StateContext<SalaryCalculationStateModel>,
    { params }: GetSalaryCalculations,
  ): Observable<ApolloQueryResult<SalaryCalculationResponse>> {
    return this.salaryCalculationService.getSalaryCalculation(params).pipe(
      tap((res) => {
        if (res && res.data) {
          ctx.patchState({
            list: res.data.getSalaryCalculation.result.map((item) => ({
              ...item,
              plannedHours: item.plannedHours * 3600,
              workedHours: item.workedHours * 3600,
            })),
            total: res.data.getSalaryCalculation.total,
          });
        }
      }),
    );
  }

  @Action(SetSalaryCalculationFilter)
  public SetSalaryCalculationFilter(
    ctx: StateContext<SalaryCalculationStateModel>,
    { filter }: SetSalaryCalculationFilter,
  ): void {
    const filterToUse = {
      ...ctx.getState().filter,
      ...filter,
    };
    ctx.patchState({
      filter: filterToUse,
    });
  }

  @Action(SetSalaryCalculationDateFilter)
  public SetSalaryCalculationDateFilter(
    ctx: StateContext<SalaryCalculationStateModel>,
    { dateFilter }: SetSalaryCalculationDateFilter,
  ): void {
    ctx.patchState({
      rawDateFilter: dateFilter,
    });
  }

  @Action(SetSalaryCalculationPaginationParams)
  public SetSalaryCalculationPaginationParams(
    ctx: StateContext<SalaryCalculationStateModel>,
    { paginationParams }: SetSalaryCalculationPaginationParams,
  ): void {
    ctx.patchState({
      pagination: paginationParams,
    });
  }

  @Action(SetSalaryCalculationSort)
  public SetSalaryCalculationSort(
    ctx: StateContext<SalaryCalculationStateModel>,
    { sort }: SetSalaryCalculationSort,
  ): void {
    ctx.patchState({
      sort,
    });
  }
}
